import { ReactIntegration } from "@grafana/faro-react"
import { type Faro, getWebInstrumentations, initializeFaro, LogLevel } from "@grafana/faro-web-sdk"

import { Environment, resolveEnvironment } from "../env"
import type { SubComponent } from "./monitoring"

let faro: Faro | undefined

/**
 * Faro default serializer runs String() over every argument, so `logger.info("msg", { ... })`
 * arrives in Loki as "msg [object Object]". Objects are stringified to keep the payload readable,
 * Errors are left to String() because JSON.stringify(new Error("x")) returns "{}".
 */
function logArgsSerializer(args: unknown[]): string {
  return args
    .map((arg) => {
      if (arg instanceof Error || typeof arg !== "object" || arg === null) {
        return String(arg)
      }

      try {
        return JSON.stringify(arg)
      } catch {
        return String(arg)
      }
    })
    .join(" ")
}

/** Vrátí inicializovanou Faro instanci (nebo undefined mimo prod / před initFaro). */
export function getFaro(): Faro | undefined {
  return faro
}

/**
 * Grafana Faro - FE logy/errory do self-hosted Lokiho přes Alloy faro.receiver.
 *
 * Fáze 1 (PoC): běží PARALELNĚ se Sentry (viz initSentry). Cílem je ověřit sběr
 * logů a chyb do Lokiho, ne vypnout Sentry. Traces jsou mimo scope (fáze 2),
 * proto zde záměrně NENÍ TracingInstrumentation.
 */
export function initFaro(subcomponent: SubComponent) {
  const environment = resolveEnvironment()
  // Běží na prod/dev/preview, ne na localhostu ani neznámém hostu.
  if (environment === Environment.Unknown) {
    return
  }

  faro = initializeFaro({
    // Alloy faro.receiver - veřejný ingest endpoint (viz infra: kubernetes_alloy.tf).
    url: "https://faro.svc.instacover.ai/collect",
    app: {
      name: "instacar",
      version: process.env.SENTRY_RELEASE ?? "not-set",
      environment, // "production" | "development" | "preview" (viz env label v Lokim)
    },
    instrumentations: [
      // Errors, unhandled rejections, web-vitals, console capture.
      ...getWebInstrumentations({ captureConsole: true }),
      new ReactIntegration(),
    ],
    // console.debug/trace/log se nesbírá (default Faro), errory ano.
    consoleInstrumentation: {
      disabledLevels: [LogLevel.DEBUG, LogLevel.TRACE, LogLevel.LOG],
    },
    logArgsSerializer,
  })

  // Obdoba Sentry tagů component/subcomponent.
  faro.api.setSession({ attributes: { component: "instacar", subcomponent } })
}

/**
 * Zaregistruje business sessionId (a zákazníka) do Faro session atributů, aby
 * FE logy v Lokim šlo korelovat se server/worker logy přes stejný sessionId
 * (obdoba registerSessionToEvents pro Sentry). Projeví se jako session_attr_sessionId.
 */
export function registerFaroSession(params: { id: string; customerId: string; customerName: string }) {
  faro?.api.setSession({
    attributes: {
      sessionId: params.id,
      customerId: params.customerId,
      customerName: params.customerName,
      component: "instacar",
      subcomponent: "client",
    },
  })
}
